/**
 * Add / Edit Admin User Component
 * ------------------------------------
 * Handles:
 * - Creating new admin users
 * - Editing existing admin users
 * - Updating verification status
 * - Managing order types (multi-select)
 * - Resetting form and clearing edit mode
 */

import React, { useState, useEffect } from "react";
import {
    TextField,
    Button,
    MenuItem,
    Chip,
    Select,
    InputLabel,
    FormControl,
    OutlinedInput,
    Box,
    Grid,
    FormControlLabel,
    Switch,
    SelectChangeEvent,
    Paper,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableBody,
    TableCell,
    Autocomplete,
} from "@mui/material";

import AdminApi from "Service/Admin.api";
import CaseApi from "Service/Case.api";
import { IndianStates } from "Constants/Arrays";
import { StyledTableCell } from "./ProfessionalUserList";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';

/* ------------------------------
   INTERFACES
------------------------------ */
interface OrderType {
    id: number;
    name: string;
}

interface AdminFormType {
    firstName: string;
    lastName: string;
    address: string;
    city: string;
    state: string;
    postalCode: string;
    dateOfBirth: string;
    password?: string;
    confirmPassword?: string;
    highestQualification: string;
    email: string;
    contactNumber: string;
    orderType: number[];
    // orderTypeIds: number[];
    verified: boolean;
}

interface OrderTypeItem {
    id: number;
    name: string;
}

interface AdminType {
    id: number;
    firstName: string;
    lastName: string;
    address: string;
    city: string;
    state: string;
    postalCode: string;
    dateOfBirth: string;
    email: string;
    contactNumber: string;
    orderTypes?: OrderTypeItem[];
    highestQualification?: string;
    verified?: boolean;
}

/* ------------------------------
   MAIN COMPONENT
------------------------------ */

const AddAdminUser: React.FC = () => {
    const navigate = useNavigate();

    /* ----------------------------------
       LOCAL STATE
    ---------------------------------- */
    const [formData, setFormData] = useState<AdminFormType>({
        firstName: "",
        lastName: "",
        address: "",
        city: "",
        state: "",
        postalCode: "",
        dateOfBirth: "",
        password: "",
        confirmPassword: "",
        email: "",
        contactNumber: "",
        highestQualification: "",
        orderType: [],
        // orderTypeIds: [],
        verified: true, // Default verified ON for new admins
    });

    const [adminList, setAdminList] = useState<AdminType[]>([]);
    const [orderTypes, setOrderTypes] = useState<OrderType[]>([]);
    const [editId, setEditId] = useState<number | null>(null);

    /* ----------------------------------
       EFFECTS - Load Data Once
    ---------------------------------- */
    useEffect(() => {
        loadAdmins();
        loadOrderTypes();
    }, []);

    /* ----------------------------------
       Fetch Admin List
    ---------------------------------- */
    const loadAdmins = async () => {
        try {
            const res = await AdminApi.getAllAdminUsers();
            setAdminList(res.data);
        } catch (error) {
            console.error("Error fetching admins:", error);
        }
    };

    /* ----------------------------------
       Fetch Order Type List
    ---------------------------------- */
    const loadOrderTypes = async () => {
        try {
            const res = await CaseApi.fetchOrderTypes();
            setOrderTypes(res.data);
        } catch (error) {
            console.error("Error fetching order types:", error);
        }
    };

    /* ----------------------------------
       Handle Basic Form Input Change
    ---------------------------------- */
    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    /* ----------------------------------
       Multi-Select (Order Types)
    ---------------------------------- */
    const handleOrderTypeChange = (
        event: SelectChangeEvent<typeof formData.orderType>
    ) => {
        const selectedIds = event.target.value as number[];
        setFormData({ ...formData, orderType: selectedIds });
    };

    /* ----------------------------------
       Verified Toggle
    ---------------------------------- */
    const handleVerifiedToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFormData((prev) => ({
            ...prev,
            verified: event.target.checked,
        }));
    };

    /* ----------------------------------
       Reset Form (Reusable)
    ---------------------------------- */
    const handleReset = () => {
        setFormData({
            firstName: "",
            lastName: "",
            address: "",
            city: "",
            state: "",
            postalCode: "",
            dateOfBirth: "",
            password: "",
            confirmPassword: "",
            email: "",
            contactNumber: "",
            highestQualification: "",
            orderType: [],
            // orderTypeIds: [],
            verified: true,
        });
        setEditId(null);
    };

    /* ----------------------------------
       Submit Form (Create / Update)
    ---------------------------------- */
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        const payload = {
            ...formData,
            confirmPassword: formData.password,
            orderTypeIds: formData.orderType,
        };

        try {
            if (editId) {
                // If password is not updated, remove it
                if (!payload.password) delete payload.password;

                await AdminApi.UpdateAdminUser(editId, {
                    ...payload,
                    id: editId,
                });
            } else {
                await AdminApi.CreateAdminUser(payload);
            }

            await loadAdmins();
            handleReset();
        } catch (error) {
            console.error("Error saving admin:", error);
        }
    };

    /* ----------------------------------
       Handle Edit (Prefill Form)
    ---------------------------------- */
    const handleEdit = (item: AdminType) => {
        setEditId(item.id);

        setFormData({
            firstName: item.firstName,
            lastName: item.lastName,
            address: item.address,
            city: item.city,
            state: item.state,
            postalCode: item.postalCode,

            // Convert API date -> YYYY-MM-DD
            dateOfBirth: item.dateOfBirth ? item.dateOfBirth.split("T")[0] : "",

            password: "",
            confirmPassword: "",
            email: item.email,
            contactNumber: item.contactNumber,
            highestQualification: item.highestQualification || "",

            // Extract IDs from orderTypes
            orderType: item.orderTypes?.map((o) => o.id) || [],
            // orderTypeIds: item.orderTypes?.map((o) => o.id) || [],
            verified: item.verified ?? true,
        });
    };

    /* ----------------------------------
       RENDER
    ---------------------------------- */
    return (
        <Box sx={{
            py: 4,
            pb: 12,               // Added extra padding at bottom for better visibility
            display: "flex",
            flexDirection: "column",
            height: "100vh",      // LIMITS height to screen size
            overflowY: "auto",    // ALLOWS scrolling within this container
        }}>
            {/* 3. ADDED BACK BUTTON HERE */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Button 
                    startIcon={<ArrowBackIcon />} 
                    onClick={() => navigate(-1)}
                    sx={{ fontWeight: 'bold' }}
                >
                    Back
                </Button>
            </Box>

            <Box display="flex" justifyContent="center" mt={4} width="100%">
                <Box width="100%" maxWidth="800px" p={4} component={Paper} elevation={3} borderRadius={3}>
                    <h2 style={{ textAlign: "center", marginBottom: "20px" }}>Add / Edit Admin</h2>
                    <form onSubmit={handleSubmit}>
                        <Grid container spacing={2}>


                            {/* Row 1: First Name / Last Name */}
                            <Grid size={{ xs: 12, sm: 6 }} >
                                <TextField label="First Name" name="firstName" value={formData.firstName} onChange={handleChange} required fullWidth />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField label="Last Name" name="lastName" value={formData.lastName} onChange={handleChange} required fullWidth />
                            </Grid>

                            {/* Row 2: Address / City */}
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField label="Address" name="address" value={formData.address} onChange={handleChange} required fullWidth />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField label="City" name="city" value={formData.city} onChange={handleChange} required fullWidth />
                            </Grid>

                            {/* Row 3: State / Postal Code */}
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Autocomplete
                                    fullWidth
                                    disablePortal
                                    options={IndianStates}
                                    value={
                                        IndianStates.find((s) => s.value === formData.state) || null
                                    }
                                    onChange={(e, newValue) =>
                                        setFormData({
                                            ...formData,
                                            state: newValue ? newValue.value : "",
                                        })
                                    }
                                    getOptionLabel={(option) => option.label}
                                    renderInput={(params) => (
                                        <TextField {...params} label="State" required />
                                    )}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField label="Postal Code" name="postalCode" value={formData.postalCode} onChange={handleChange} required fullWidth />
                            </Grid>

                            {/* Row 4: DOB / Highest Qualification */}
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    label="Date Of Birth"
                                    name="dateOfBirth"
                                    type="date"
                                    value={formData.dateOfBirth}
                                    onChange={handleChange}
                                    required
                                    fullWidth
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    label="Highest Qualification"
                                    name="highestQualification"
                                    value={formData.highestQualification}
                                    onChange={handleChange}
                                    required
                                    fullWidth
                                />
                            </Grid>

                            {!editId && (
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <TextField label="Password" name="password" type="password" value={formData.password} onChange={handleChange} required fullWidth />
                                </Grid>
                            )}
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField label="Email" name="email" type="email" value={formData.email} onChange={handleChange} required fullWidth />
                            </Grid>

                            {/* Row 6: Contact / Order Type */}
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField label="Contact" name="contactNumber" type="tel" value={formData.contactNumber} onChange={handleChange} required fullWidth inputProps={{ maxLength: 10 }} />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Autocomplete
                                    multiple
                                    disablePortal
                                    fullWidth
                                    options={orderTypes}
                                    value={orderTypes.filter((o) => formData.orderType.includes(o.id))}
                                    getOptionLabel={(option) => option.name}
                                    onChange={(e, newValue) =>
                                        setFormData({
                                            ...formData,
                                            orderType: newValue.map((item) => item.id),
                                        })
                                    }
                                    filterSelectedOptions
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Order Type *"
                                            placeholder="Select Order Types"
                                        />
                                    )}
                                />
                            </Grid>

                            {editId && (
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={formData.verified}
                                                onChange={handleVerifiedToggle}
                                                color="success"
                                            />

                                        }
                                        label={formData.verified ? "Verified" : "Not Verified"}
                                    />
                                </Grid>
                            )}



                            <Grid size={{ xs: 12 }}>
                                <Grid sx={{ xs: 12, display: "flex", gap: 2 }} >
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        color="primary"
                                        fullWidth
                                        sx={{ py: 1.2, fontSize: "16px" }}
                                    >
                                        {editId ? "Update Admin" : "Create Admin"}
                                    </Button>

                                    <Button
                                        type="button"
                                        variant="outlined"
                                        color="primary"
                                        fullWidth
                                        sx={{ py: 1.2, fontSize: "16px" }}
                                        onClick={handleReset}
                                    >
                                        Cancel
                                    </Button>
                                </Grid>
                            </Grid>

                        </Grid>
                    </form>
                </Box>
            </Box>

            <h2 style={{ marginTop: "40px", textAlign: "center", }}>Admin List</h2>

            {/* LISTING TABLE */}
            <Paper sx={{px:2}}>
                <TableContainer
                    sx={{
                        maxHeight: 470,
                        overflowY: "auto",
                        border: "1px solid #e0e0e0",
                        borderRadius: 2,
                    }}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <StyledTableCell>ID</StyledTableCell>
                                <StyledTableCell>First Name</StyledTableCell>
                                <StyledTableCell>Last Name</StyledTableCell>
                                <StyledTableCell>Email</StyledTableCell>
                                <StyledTableCell>Contact</StyledTableCell>
                                <StyledTableCell>Order Types</StyledTableCell>
                                <StyledTableCell>Status</StyledTableCell>
                                <StyledTableCell align="center">Action</StyledTableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {adminList.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={4} align="center">
                                        No Users Found
                                    </TableCell>
                                </TableRow>
                            ) : (
                                adminList.map((user: any, index: number) => (
                                    <TableRow key={user.id}>
                                        <TableCell>{user.id}</TableCell>
                                        <TableCell>{user.firstName}</TableCell>
                                        <TableCell>{user.lastName}</TableCell>
                                        <TableCell>{user.email}</TableCell>
                                        <TableCell>{user.contactNumber}</TableCell>

                                        <TableCell>
                                            {user?.orderTypes?.map((t: any) => (
                                                <Chip
                                                    key={t.id}
                                                    label={t.name}
                                                    size="small"
                                                    sx={{ mr: 0.5 }}
                                                />
                                            ))}
                                        </TableCell>

                                        <TableCell>
                                            <Chip
                                                label={user.verified ? "Verified" : "Not Verified"}
                                                color={user.verified ? "success" : "error"}
                                                size="small"
                                            />
                                        </TableCell>

                                        {/* CONDITIONAL ACTION BUTTON */}
                                        <TableCell align="center">
                                            <Button variant="outlined" size="small" onClick={() => handleEdit(user)}>Edit</Button>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
        </Box>
    );
};

export default AddAdminUser;